import React, { useRef, useState, useEffect } from "react";
import { memberDisplayName, memberInitials } from "../../utils/memberDisplay";
import styles from "./LeadCard.module.css";

/**
 * LeadCard — card individual de um lead no kanban (ou lista mobile).
 *
 * Props:
 *   lead          {Object}                        — { id, name, phone, email, notes, source, updatedAt, assignedTo }
 *   assignee      {Object|null}                   — `TenantMember` resolvido a partir de `lead.assignedTo`
 *                                                   (o payload do lead traz só o uuid — quem resolve é a página)
 *   onOpen        {(lead) => void}                — abre o drawer de detalhes
 *   onMove        {(lead, toStage) => void}       — move o lead para outro stage
 *   onDelete      {(lead) => void}                — remove o lead
 *   stages        {Array<{ id, label, emoji }>}   — lista de stages para o menu "Mover para…"
 *   currentStage  {string}                        — stage atual (para esconder do menu)
 */
export default function LeadCard({
  lead,
  assignee = null,
  onOpen,
  onMove,
  onDelete,
  stages = [],
  currentStage,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setMoveOpen(false);
      }
    }
    if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const handleCardClick = (e) => {
    // Ignore clicks no menu
    if (menuRef.current && menuRef.current.contains(e.target)) return;
    onOpen?.(lead);
  };

  const handleCardKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen?.(lead);
    }
  };

  const phoneDisplay = lead.phone || null;
  const notesDisplay = (lead.notes || "").trim();
  const relativeTime = formatRelative(lead.updatedAt);
  const sourceDisplay = lead.source ? `via ${lead.source}` : null;

  return (
    <div
      className={styles.card}
      role="button"
      tabIndex={0}
      aria-label={`Abrir detalhes de ${lead.name}`}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div className={styles.header}>
        <span className={styles.name}>{lead.name}</span>
        <div className={styles.menuWrapper} ref={menuRef}>
          <button
            type="button"
            className={styles.menuBtn}
            aria-label="Ações do lead"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
              setMoveOpen(false);
            }}
          >
            ⋯
          </button>
          {menuOpen && (
            <div className={styles.menu} role="menu">
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  onOpen?.(lead);
                }}
              >
                Abrir detalhes
              </button>
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={(e) => {
                  e.stopPropagation();
                  setMoveOpen((v) => !v);
                }}
                aria-expanded={moveOpen}
              >
                Mover para…
              </button>
              {moveOpen && (
                <div className={styles.submenu} role="menu">
                  {stages
                    .filter((s) => s.id !== currentStage)
                    .map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        role="menuitem"
                        className={styles.menuItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(false);
                          setMoveOpen(false);
                          onMove?.(lead, s.id);
                        }}
                      >
                        <span aria-hidden="true">{s.emoji}</span> {s.label}
                      </button>
                    ))}
                </div>
              )}
              <button
                type="button"
                role="menuitem"
                className={`${styles.menuItem} ${styles.menuItemDanger}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  onDelete?.(lead);
                }}
              >
                Remover
              </button>
            </div>
          )}
        </div>
      </div>

      {phoneDisplay && (
        <p className={styles.line}>
          <span aria-hidden="true">📱</span> {phoneDisplay}
        </p>
      )}

      {notesDisplay && (
        <p className={`${styles.line} ${styles.notes}`} title={notesDisplay}>
          <span aria-hidden="true">💬</span> {notesDisplay}
        </p>
      )}

      {/* Responsável (discreto). Três estados: pessoa conhecida, uuid gravado
          mas membro não encontrado (removido da equipe) e ninguém atribuído. */}
      <div className={styles.assignee}>
        {assignee ? (
          <>
            <span className={styles.assigneeAvatar} aria-hidden="true">
              {memberInitials(assignee)}
            </span>
            <span className={styles.assigneeName}>
              {memberDisplayName(assignee)}
            </span>
          </>
        ) : (
          <span className={styles.assigneeEmpty}>
            {lead.assignedTo ? "Responsável não identificado" : "Sem responsável"}
          </span>
        )}
      </div>

      <div className={styles.footer}>
        {relativeTime && <span>{relativeTime}</span>}
        {relativeTime && sourceDisplay && <span aria-hidden="true"> · </span>}
        {sourceDisplay && <span>{sourceDisplay}</span>}
      </div>
    </div>
  );
}

/**
 * Formata um timestamp ISO como tempo relativo em pt-BR ("há 2 dias", "agora há pouco").
 * Sem dependência externa para evitar problemas de ESM no Jest do CRA.
 */
function formatRelative(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "agora há pouco";
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `há ${diffH} h`;
  const diffD = Math.round(diffH / 24);
  if (diffD === 1) return "há 1 dia";
  if (diffD < 30) return `há ${diffD} dias`;
  const diffMo = Math.round(diffD / 30);
  if (diffMo === 1) return "há 1 mês";
  if (diffMo < 12) return `há ${diffMo} meses`;
  const diffY = Math.round(diffMo / 12);
  return diffY === 1 ? "há 1 ano" : `há ${diffY} anos`;
}
